// 9fbef606107a605d69c0edbcd8029e5d
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import logger from '@tcp/core/src/utils/loggerInstance';
import {
  parseBoolean,
  getIconPath,
  getImageFilePath,
  getViewportInfo,
  getAPIConfig,
  getAspectRatio,
} from '@tcp/core/src/utils';
import ImageSource from '../DamImage/molecules/ImageSource.view';
import {
  getVideoUrls,
  getPosterUrls,
  getCustomPosterForCloudinary,
  findPosterImage,
} from '../DamImage/utils/DamImageUtils';

import {
  StyledVideo,
  getVideoStyle,
  StyledVideoContainer,
  StyledImageContainer,
  StyledImage,
} from '../DamImage/DamImage.styles';
import { CloudinaryVideoPlayerGlobalStyle } from './styles/CloudinaryVideoPlayer.style';

const RenderVideoPoster = ({ videoData, preloadImage, aspectRatioObj }) => {
  const videoUrls = getVideoUrls(videoData);
  const posterUrls = getPosterUrls(videoData);
  const mobilePoster = findPosterImage(posterUrls, videoUrls, 'mobile');
  const desktopPoster = findPosterImage(posterUrls, videoUrls, 'desktop');
  const defaultAspectRatioObj = getAspectRatio(null, videoData, null);
  return (
    <>
      <picture>
        <ImageSource
          srcSet={[{ url: desktopPoster }]}
          media="(min-width: 768px)"
          preloadImage={preloadImage}
        />
        <ImageSource
          srcSet={[{ url: mobilePoster }]}
          media="(max-width: 767px)"
          preloadImage={preloadImage}
        />
        <StyledImage
          src={mobilePoster}
          alt={videoData.title}
          aspectRatioObj={{ ...defaultAspectRatioObj, ...aspectRatioObj }}
        />
      </picture>
    </>
  );
};

RenderVideoPoster.propTypes = {
  videoData: PropTypes.shape({
    autoplay: PropTypes.string,
    muted: PropTypes.string,
    controls: PropTypes.string,
    playsinline: PropTypes.string,
    loop: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  preloadImage: PropTypes.bool,
  aspectRatioObj: PropTypes.shape({}),
};

RenderVideoPoster.defaultProps = {
  preloadImage: false,
  aspectRatioObj: {},
};

const getUniqueID = () => {
  if (process.env.NODE_ENV === 'test') {
    return 'video_test_id';
  }
  return `video_${Date.now() + (Math.random() * 100000).toFixed()}`;
};

const getPlayIcon = (isModule) =>
  isModule ? `${getImageFilePath()}/play-accessible.svg` : getIconPath('icon-play');

const getPauseIcon = (isModule) =>
  isModule ? `${getImageFilePath()}/pause-accessible.svg` : getIconPath('icon-pause');

const getMuteIcon = (isModule) => isModule && `${getImageFilePath()}/mute.svg`;

const getUnmuteIcon = (isModule) => isModule && `${getImageFilePath()}/unmute.svg`;

const RenderMuteIcon = (playing, toggleMute, muteIconPath) => {
  return (
    playing && (
      <button
        className="video-mute-btn"
        type="button"
        title="Mute Video"
        aria-disabled="false"
        onClick={(e) => {
          e.preventDefault();
          toggleMute();
        }}
      >
        <img className="mute_button_icon" alt="" aria-hidden="true" src={muteIconPath} />
      </button>
    )
  );
};

const RenderPauseButton = (playing, toggleVideo, iconPath) => {
  return (
    playing && (
      <button
        className="video-play-button"
        type="button"
        title="Pause Video"
        aria-disabled="false"
        onClick={(e) => {
          e.preventDefault();
          toggleVideo();
        }}
      >
        <img
          className="tcp_carousel__play_pause_button_icon"
          alt=""
          aria-hidden="true"
          src={iconPath}
        />
      </button>
    )
  );
};

const RenderBigPlayButton = (playing, toggleVideo, playIcon) => {
  return (
    !playing && (
      <button
        className="big-video-play-btn"
        type="button"
        title="Play Video"
        aria-disabled="false"
        onClick={(e) => {
          e.preventDefault();
          toggleVideo();
        }}
      >
        <img
          className="tcp_carousel__big_play_button_icon"
          alt=""
          aria-hidden="true"
          src={playIcon}
        />
      </button>
    )
  );
};

const callToggleMute = ({
  isMuted,
  vPlayer,
  setIsMuted,
  setMuteIconPath,
  muteIcon,
  unmuteIcon,
}) => {
  if (isMuted === true) {
    // eslint-disable-next-line no-param-reassign
    vPlayer.unmute();
    setIsMuted(false);
    setMuteIconPath(unmuteIcon);
  } else {
    // eslint-disable-next-line no-param-reassign
    vPlayer.mute();
    setIsMuted(true);
    setMuteIconPath(muteIcon);
  }
};

const getDamCloudName = (apiConfig) => {
  const { damCloudName } = apiConfig;
  return damCloudName;
};

const getSecureDistribution = (apiConfig) => {
  const { assetHostTCP } = apiConfig;
  return assetHostTCP?.split('/')[2];
};

const getAutoPlay = (booleanValues) => booleanValues.autoplay && booleanValues.muted;

const getSourceAndPoster = (isDesktop, isTablet, isMobile, videoUrls, posterUrls) => {
  let source = '';
  let poster = '';

  if (isDesktop && videoUrls.desktop) {
    source = videoUrls.desktop;
    poster = getCustomPosterForCloudinary(posterUrls, 'desktop');
  } else if (isTablet && videoUrls.tablet) {
    source = videoUrls.tablet;
    poster = getCustomPosterForCloudinary(posterUrls, 'tablet');
  } else if (isMobile && videoUrls.mobile) {
    source = videoUrls.mobile;
    poster = getCustomPosterForCloudinary(posterUrls, 'mobile');
  }

  return { computedSource: source, computedPoster: poster };
};

const setupPlayerEvents = (player, updateVideoLoaded, setPlaying) => {
  player.on('error', (e) => {
    // eslint-disable-next-line no-underscore-dangle
    if (e?.Player?.videojs?.error_) {
      logger.error(
        // eslint-disable-next-line no-underscore-dangle
        `Status Code-${e.Player.videojs.error_.statusCode}: Message-${e.Player.videojs.error_.message}`
      );
    }
  });

  ['loadedmetadata', 'canplaythrough', 'canplay'].forEach((event) => {
    player.on(event, updateVideoLoaded);
  });

  player.on('play', () => setPlaying(true));
  player.on('pause', () => setPlaying(false));
};

const initializePlayer = (
  videoPlayer,
  videoRef,
  apiConfig,
  booleanValues,
  poster,
  source,
  updateVideoLoaded,
  setPlaying,
  setVplayer
) => {
  if (typeof videoPlayer.perSourceBehaviors === 'function') {
    videoPlayer.perSourceBehaviors();
  } else {
    console.error('perSourceBehaviors is not a function on the videojs object');
  }

  const player = videoPlayer(videoRef.current, {
    cloud_name: getDamCloudName(apiConfig),
    secure: true,
    private_cdn: true,
    secure_distribution: getSecureDistribution(apiConfig),
    fluid: true,
    muted: booleanValues.muted,
    autoplay: getAutoPlay(booleanValues),
    loop: booleanValues.loop,
    controls: booleanValues.controls,
    preload: 'metadata',
    bigPlayButton: false,
    showLogo: false,
    posterOptions: { publicId: poster || '' },
  });

  setupPlayerEvents(player, updateVideoLoaded, setPlaying);
  player.source(source, { sourceTypes: ['auto'] });
  setVplayer(player);
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function CloudinaryVideoPlayer({
  videoData,
  isModule,
  preloadImage,
  assignKeyValue,
  makePositionAbsolute,
  isEnableMute,
}) {
  const playIcon = getPlayIcon(isModule);
  const pauseIcon = getPauseIcon(isModule);
  const muteIcon = getMuteIcon(isModule);
  const unmuteIcon = getUnmuteIcon(isModule);
  const videoUrls = getVideoUrls(videoData);
  const posterUrls = getPosterUrls(videoData);
  const {
    autoplay = true,
    playsinline: playsInline = true,
    muted = true,
    controls = false,
    loop = true,
    title = 'Video',
  } = videoData;

  const booleanValues = {
    autoplay: parseBoolean(autoplay),
    playsInline: parseBoolean(playsInline),
    muted: parseBoolean(muted),
    controls: parseBoolean(controls),
    loop: parseBoolean(loop),
  };
  const defaultMuteIcon = parseBoolean(muted) ? muteIcon : unmuteIcon;
  const defaultAutoPlay = parseBoolean(autoplay) && parseBoolean(muted);
  const apiConfig = getAPIConfig();

  const [uniqueId] = useState(getUniqueID());
  const [playing, setPlaying] = useState(defaultAutoPlay);
  const [isMuted, setIsMuted] = useState(parseBoolean(muted));
  const [muteIconPath, setMuteIconPath] = useState(defaultMuteIcon);
  const [iconPath] = useState(pauseIcon);
  const [vPlayer, setVplayer] = useState();
  const [videoLoaded, setVideoLoaded] = useState(false);

  const videoRef = useRef(null);

  let source = videoUrls?.desktop;
  let poster = getCustomPosterForCloudinary(posterUrls, 'desktop');
  const aspectRatioObj = getAspectRatio(null, videoData, null);

  const toggleVideo = () => {
    if (playing === true) {
      vPlayer.pause();
      setPlaying(false);
    } else {
      vPlayer.play();
      setPlaying(true);
    }
  };

  const toggleMute = () => {
    callToggleMute({ isMuted, vPlayer, setIsMuted, setMuteIconPath, muteIcon, unmuteIcon });
  };

  const updateVideoLoaded = () => {
    if (!videoLoaded) {
      setVideoLoaded(true);
    }
  };

  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      const { top, bottom } = videoRef.current.getBoundingClientRect();
      const isInViewport = top >= 0 && bottom <= window.innerHeight;

      if (isInViewport) {
        videoRef.current.play().catch(() => {
          console.log('Autoplay prevented by browser policy.');
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', handlePlayPause);
    return () => {
      document.removeEventListener('visibilitychange', handlePlayPause);
    };
  }, []);

  useEffect(() => {
    let player;
    const { isDesktop, isTablet, isMobile } = getViewportInfo();
    const { computedSource, computedPoster } = getSourceAndPoster(
      isDesktop,
      isTablet,
      isMobile,
      videoUrls,
      posterUrls
    );
    source = computedSource;
    poster = computedPoster;
    if (videoRef?.current && !vPlayer) {
      import('cloudinary-video-player').then(({ videoPlayer }) => {
        initializePlayer(
          videoPlayer,
          videoRef,
          apiConfig,
          booleanValues,
          poster,
          source,
          updateVideoLoaded,
          setPlaying,
          setVplayer
        );
      });
    }

    return () => {
      if (player) {
        player?.dispose?.();
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && videoRef.current) {
        if (!videoRef.current.paused) {
          videoRef.current.play().catch((error) => {
            videoRef.current.pause();
            console.log('Error playing video:', error);
          });
        } else {
          videoRef.current.pause();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <StyledVideo
      isModule={isModule}
      overrideStyle={getVideoStyle(videoData)}
      key={assignKeyValue}
      aspectRatioObj={aspectRatioObj}
      makePositionAbsolute={makePositionAbsolute}
    >
      <React.Fragment key={assignKeyValue}>
        <StyledImageContainer aspectRatioObj={aspectRatioObj}>
          {videoLoaded ? null : (
            <RenderVideoPoster
              videoData={videoData}
              preloadImage={preloadImage}
              aspectRatioObj={aspectRatioObj}
            />
          )}
        </StyledImageContainer>
      </React.Fragment>
      <CloudinaryVideoPlayerGlobalStyle />
      <React.Fragment key={assignKeyValue}>
        <StyledVideoContainer videoLoaded={videoLoaded} aspectRatioObj={aspectRatioObj}>
          <video
            className={uniqueId}
            playsInline={parseBoolean(playsInline)}
            title={title}
            ref={videoRef}
          >
            <track kind="captions" />
          </video>
          {RenderBigPlayButton(playing, toggleVideo, playIcon)}
          {RenderPauseButton(playing, toggleVideo, iconPath)}
          {isEnableMute && RenderMuteIcon(playing, toggleMute, muteIconPath)}
        </StyledVideoContainer>
      </React.Fragment>
    </StyledVideo>
  );
}

CloudinaryVideoPlayer.propTypes = {
  videoData: PropTypes.shape({
    autoplay: PropTypes.string,
    muted: PropTypes.string,
    controls: PropTypes.string,
    playsinline: PropTypes.string,
    loop: PropTypes.string,
    title: PropTypes.string,
  }),
  isModule: PropTypes.bool.isRequired,
  assignKeyValue: PropTypes.string.isRequired,
  preloadImage: PropTypes.bool,
  makePositionAbsolute: PropTypes.bool,
  isEnableMute: PropTypes.bool,
};

CloudinaryVideoPlayer.defaultProps = {
  videoData: {
    autoplay: '1',
    muted: '1',
    controls: '1',
    loop: '1',
    title: 'Video',
    playsinline: '1',
  },
  preloadImage: false,
  makePositionAbsolute: false,
  isEnableMute: false,
};

export default CloudinaryVideoPlayer;
export { CloudinaryVideoPlayer as CloudinaryVideoPlayerVanilla };
